import React, { useEffect } from 'react';
import { Shield, AlertCircle, FileText, Scale, Users, Globe, Mail, Calendar } from 'lucide-react';

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: FileText,
      title: '1. Agreement to Terms',
      content: `
        By accessing or using any services provided by HyperionAI ("Company," "we," "us," or "our"), including but not limited to our website, applications, software development services, consulting services, and any related services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, then you may not access our Services.
        
        These Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you"), and HyperionAI, concerning your access to and use of our Services. You represent that you have the authority to bind yourself or the entity you represent to these Terms.
        
        We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Your continued use of our Services following the posting of revised Terms means that you accept and agree to the changes.
      `
    },
    {
      icon: Shield,
      title: '2. Intellectual Property Rights',
      content: `
        All intellectual property rights in and to the Services, including but not limited to software, applications, content, designs, text, graphics, logos, images, and all source code and object code (collectively, "Intellectual Property"), are owned by or licensed to HyperionAI.
        
        Work Product Ownership: Unless otherwise specified in a separate written agreement:
        • Custom software and applications developed specifically for you remain your property upon full payment
        • We retain ownership of our pre-existing intellectual property, methodologies, and general-purpose tools
        • We retain the right to use general knowledge, skills, and experience gained during the project
        • You grant us a limited license to showcase the work in our portfolio with your prior consent
        
        Open Source Components: Some of our solutions may incorporate open-source software components. Such components are subject to their respective open-source licenses, which will be identified in the project documentation.
        
        Confidentiality: Both parties agree to maintain the confidentiality of any proprietary or confidential information disclosed during the course of our relationship. This obligation survives the termination of these Terms.
      `
    },
    {
      icon: Users,
      title: '3. User Responsibilities and Acceptable Use',
      content: `
        By using our Services, you agree to:
        • Provide accurate, current, and complete information as requested
        • Maintain the security and confidentiality of your account credentials
        • Promptly notify us of any unauthorized use of your account
        • Comply with all applicable laws and regulations
        • Not use the Services for any illegal or unauthorized purpose
        • Not interfere with or disrupt the Services or servers
        • Not attempt to gain unauthorized access to any portion of the Services
        • Not reverse engineer, decompile, or disassemble any software
        • Cooperate with reasonable requests for information and feedback
        • Make timely payments for all Services rendered
        
        You are solely responsible for:
        • Your use of the Services and any consequences thereof
        • Content you provide or transmit through the Services
        • Compliance with applicable industry standards and regulations
        • Backing up your data and maintaining appropriate security measures
      `
    },
    {
      icon: Scale,
      title: '4. Service Terms and Deliverables',
      content: `
        Project Scope: The specific scope, deliverables, timelines, and fees for each project will be outlined in a separate Statement of Work (SOW) or project agreement. These Terms supplement any such agreements.
        
        Development Process:
        • We follow industry-standard development methodologies
        • Regular progress updates will be provided as agreed
        • Your timely feedback and approvals are essential for project success
        • Changes to project scope may result in timeline and fee adjustments
        
        Acceptance Criteria:
        • Deliverables are deemed accepted upon: (a) written acceptance, (b) use in production, or (c) 15 days after delivery without written objection
        • Minor bugs and issues will be addressed according to our maintenance agreement
        • Feature requests beyond the original scope require separate agreements
        
        Support and Maintenance:
        • Post-launch support terms are defined in separate support agreements
        • We provide documentation and knowledge transfer as specified in the SOW
        • Emergency support may be available at additional rates
      `
    },
    {
      icon: AlertCircle,
      title: '5. Limitations of Liability',
      content: `
        TO THE MAXIMUM EXTENT PERMITTED BY LAW:
        
        No Warranties: THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
        
        Limitation of Liability: IN NO EVENT SHALL HYPERIONAI, ITS AFFILIATES, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
        
        Cap on Liability: OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT PAID BY YOU FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE THOUSAND DOLLARS ($1,000).
        
        Exceptions: Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability, so some of the above limitations may not apply to you.
        
        Risk Allocation: These limitations reflect the allocation of risk between the parties and are a fundamental element of the basis of the bargain between us.
      `
    },
    {
      icon: Globe,
      title: '6. Indemnification',
      content: `
        You agree to defend, indemnify, and hold harmless HyperionAI, its affiliates, and their respective officers, directors, employees, contractors, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
        
        • Your violation of these Terms
        • Your violation of any third-party rights, including intellectual property rights
        • Your use or misuse of the Services
        • Content you provide or transmit through the Services
        • Your violation of any applicable laws or regulations
        • Any breach of your representations and warranties
        • Your negligence or willful misconduct
        
        We reserve the right to assume exclusive defense and control of any matter subject to indemnification by you, in which case you agree to cooperate with our defense of such claim.
        
        This indemnification obligation will survive the termination or expiration of these Terms and your use of the Services.
      `
    },
    {
      icon: Calendar,
      title: '7. Term and Termination',
      content: `
        Term: These Terms remain in effect as long as you use our Services or until terminated by either party.
        
        Termination for Convenience: Either party may terminate the service relationship with 30 days written notice, subject to completion of ongoing projects and payment obligations.
        
        Termination for Cause: Either party may terminate immediately upon written notice if the other party:
        • Materially breaches these Terms and fails to cure within 10 days of notice
        • Becomes insolvent or subject to bankruptcy proceedings
        • Engages in illegal activities or gross negligence
        
        Effects of Termination:
        • All payment obligations for Services rendered become immediately due
        • Each party must return or destroy the other party's confidential information
        • You retain ownership of paid-for deliverables
        • Provisions that by their nature should survive termination will remain in effect
        
        Post-Termination Support: We may provide transitional support services at our standard rates to facilitate orderly transition.
      `
    },
    {
      icon: Mail,
      title: '8. Dispute Resolution and Governing Law',
      content: `
        Governing Law: These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to its conflict of law provisions.
        
        Informal Resolution: Before filing a claim, each party agrees to try to resolve the dispute informally by contacting the other party in writing with a notice of the dispute, a brief description, and the relief requested.
        
        Arbitration: If informal resolution fails, any dispute shall be finally settled by binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association. The arbitration shall be conducted in Delaware, and judgment may be entered in any court of competent jurisdiction.
        
        Exceptions to Arbitration: Either party may seek injunctive or other equitable relief in any court of competent jurisdiction for:
        • Intellectual property infringement
        • Breach of confidentiality obligations
        • Violation of applicable laws
        
        Class Action Waiver: YOU AGREE THAT ANY DISPUTES SHALL BE BROUGHT IN YOUR INDIVIDUAL CAPACITY ONLY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
        
        Attorney's Fees: The prevailing party in any dispute shall be entitled to recover its reasonable attorneys' fees and costs.
        
        Contact Information:
        For all legal notices and disputes:
        HyperionAI Legal Department
        Email: support@hyperionai-ltd.com
        Response time: Within 5 business days
      `
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg pt-24 pb-20">
      <div className="max-w-4xl mx-auto content-padding">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Terms of <span className="brand-gradient">Service</span>
          </h1>
          <p className="text-dark-muted">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-dark-surface border border-dark-border rounded-xl p-6 mb-8">
          <p className="text-dark-muted leading-relaxed">
            Welcome to HyperionAI. These Terms of Service govern your use of our services and establish the legal agreement 
            between you and HyperionAI. Please read these terms carefully before engaging our services. By using our services, 
            you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} className="bg-dark-surface border border-dark-border rounded-xl p-6 hover:border-brand/30 transition-colors duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                </div>
                <div className="text-dark-muted leading-relaxed whitespace-pre-line pl-14">
                  {section.content.trim()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Information */}
        <div className="mt-12 bg-gradient-to-br from-brand/10 to-brand-light/10 border border-brand/30 rounded-xl p-8">
          <h3 className="text-2xl font-semibold mb-4">Questions About These Terms?</h3>
          <p className="text-dark-muted mb-6">
            If you have any questions about these Terms of Service or need clarification on any points, 
            please don't hesitate to contact our legal team. We're here to ensure you fully understand 
            the terms of our relationship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@hyperionai-ltd.com?subject=Terms of Service Inquiry"
              className="action-btn inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact Legal Team
            </a>
            <button 
              onClick={() => window.print()}
              className="outline-btn inline-flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Print Terms
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-dark-muted">
          <p>
            © {new Date().getFullYear()} HyperionAI. All rights reserved. These Terms of Service are legally binding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;

